//@flow

import React, { useState } from "react";

import { classNameOf, LargeFramePreRenderer, positionStickyFallback } from "@/utils";

type InnovationAnimationProps = {
    progress: number,
    frames: InnovationFrame[],
    firstFrameLoaded: () => void,
};

type InnovationBackgroundState = {
    context?: CanvasRenderingContext2D,
    renderer?: LargeFramePreRenderer,
};

const ANIMATION_CONTAINER_HEIGHT = 1000; // 1000vh;

/**
 * Animated scrolling cursor component.
 * Fades until the user scrolls at least 100vh, after this the animation is hidden until the page is refreshed.
 *
 * @param progress
 * @return {JSX.Element}
 * @constructor
 */
const ScrollCursor = ({ progress }: { progress: number }) => {
    const end = 100 / ANIMATION_CONTAINER_HEIGHT; // End when user scrolls down 100vh;
    const fraction = 1 - Math.max(0, Math.min(1, (1 / end) * progress));
    const [didScroll, setDidScroll] = useState(fraction <= 0);
    if (didScroll === false && fraction <= 0) {
        setDidScroll(true);
    }
    function onClick() {
        window.scrollTo({
            top: window.innerHeight,
            behavior: "smooth",
        });
    }

    return (
        <div
            tabIndex={0}
            className={classNameOf("scroll-cursor", {
                "did-scroll": didScroll,
            })}
            style={{ opacity: fraction }}
            onClick={onClick}
        />
    );
};

class InnovationBackground extends React.Component<InnovationAnimationProps, InnovationBackgroundState> {
    +ref: {current: HTMLCanvasElement | null} = React.createRef<HTMLCanvasElement>();

    constructor(props: any) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        const canvas = this.ref.current;
        if (canvas == null) {
            return;
        }
        LargeFramePreRenderer.create(this.props.frames).then(renderer => {
            canvas.width = renderer.frameWidth;
            canvas.height = renderer.frameHeight;
            const context = canvas.getContext("2d", {
                alpha: false,
                antialias: false,
            });
            context.font = "25px monospace";
            context.fillStyle = "#ff0101";
            context.imageSmoothingEnabled = false;

            this.props.firstFrameLoaded();
            this.setState(state => ({
                ...state,
                context,
                renderer,
            }));
        });
    }

    shouldComponentUpdate(
        nextProps: InnovationAnimationProps,
        nextState: InnovationBackgroundState,
    ): boolean {
        if (nextState.context !== this.state.context) {
            return true;
        }
        const prevFrame = Math.round(
            this.props.frames.length * this.props.progress,
        );
        const nextFrame = Math.round(
            nextProps.frames.length * nextProps.progress,
        );

        return prevFrame !== nextFrame;
    }

    componentDidUpdate() {
        const { context, renderer } = this.state;
        const { progress, frames } = this.props;
        if (context == null || renderer == null) return;
        renderer.renderFrame(Math.round(frames.length * progress), context);
        // Draw the current scroll VH to the top right of the canvasx
        // context.fillText(String(progress * ANIMATION_CONTAINER_HEIGHT), 50, 50);
    }

    render(): React$Element<"canvas"> {
        return <canvas ref={this.ref} />;
    }
}

function calculateProgress(
    start: number,
    end: number,
    current: number,
): number {
    return (current - start) / (end - start);
}

function easeInOut(progress: number) {
    return Math.sin(progress * 0.5 * Math.PI);
}

function opacityTimingFunction(progress: number) {
    if (progress <= 0.2) {
        return progress / 0.2;
    }
    if (progress >= 0.8) {
        return 1 - (progress - 0.8) / (1 - 0.8);
    }
    return 1;
}

function firstOpacityTimingFunction(progress: number) {
    return 1 - progress;
}

function lastOpacityTimingFunction(progress: number) {
    return progress;
}

function matrixTimingFunction(progress: number) {
    return (1 - progress) * 56 - 29;
}

function firstMatrixTimingFunction(progress: number) {
    return progress * -29;
}

function lastMatrixTimingFunction(progress: number) {
    return (1 - progress) * 27;
}

class InnovationForeground extends React.Component<{ progress: number }> {
    +frames: any[];

    constructor(props: { progress: number }) {
        super(props);

        this.frames = [
            {
                from: 0 / ANIMATION_CONTAINER_HEIGHT,
                to: 50 / ANIMATION_CONTAINER_HEIGHT,
                opacity: firstOpacityTimingFunction,
                matrix: firstMatrixTimingFunction,
                text: "Imagine",
            },
            {
                from: 150 / ANIMATION_CONTAINER_HEIGHT,
                to: 225 / ANIMATION_CONTAINER_HEIGHT,
                opacity: opacityTimingFunction,
                matrix: matrixTimingFunction,
                text: "Any Fabric",
            },
            {
                from: 340 / ANIMATION_CONTAINER_HEIGHT,
                to: 550 / ANIMATION_CONTAINER_HEIGHT,
                opacity: opacityTimingFunction,
                matrix: matrixTimingFunction,
                text: "Any Room",
            },
            {
                from: 720 / ANIMATION_CONTAINER_HEIGHT,
                to: 1000 / ANIMATION_CONTAINER_HEIGHT,
                opacity: lastOpacityTimingFunction,
                matrix: lastMatrixTimingFunction,
                text: "Endless Possibilities",
            },
        ];
    }

    render(): React$Element<"div"> {
        const { progress } = this.props;
        const titleClassName =
            "display-3 line-height-xs text-white mb-0 top w-100 scroll-fade-title";

        const text = this.frames.map((frame, index) => {
            if (progress >= frame.from && progress <= frame.to) {
                const fraction = easeInOut(
                    calculateProgress(frame.from, frame.to, progress),
                );
                const style = {
                    opacity: frame.opacity(fraction),
                    transform: `matrix(1, 0, 0, 1, 0, ${frame.matrix(
                        fraction,
                    )})`,
                };
                return (
                    <div key={index} style={style} className={titleClassName}>
                        {frame.text}
                    </div>
                );
            } else {
                const style = {
                    opacity: 0,
                    transform: "matrix(1, 0, 0, 1, 0, 0)",
                };
                return (
                    <div key={index} style={style} className={titleClassName}>
                        {frame.text}
                    </div>
                );
            }
        });

        return (
            <div className="row vh-100 align-items-center">
                <div className="col-12 text-center">
                    <div className="position-relative user-select-none">
                        <h1 className="sr-only">Innovations</h1>

                        {text}
                    </div>
                </div>
            </div>
        );
    }
}

type FrameSize = {
    original: string,
    compressed: string,
};

export type InnovationFrame = {
    large: FrameSize,
    sm: FrameSize,
};

type Props = {
    frames: InnovationFrame[],
};

class InnovationAnimation
    extends React.Component<
    Props,
    { progress: number, firstFrameLoaded: boolean },
  > {
    +containerRef: { current: HTMLElement | null } = React.createRef<
    HTMLElement,
  >();
    +wrapperRef: { current: HTMLElement | null } = React.createRef<HTMLElement>();
  
    constructor(props: Props) {
        super(props);
        this.state = {
            progress: 0,
            firstFrameLoaded: false,
        };
    }
  
    componentDidMount() {
        document.addEventListener("scroll", this.onScroll.bind(this));
        const wrapper = this.wrapperRef.current;
        if (wrapper == null) return;
        positionStickyFallback(wrapper);
    }
  
    componentWillUnmount() {
        document.removeEventListener("scroll", this.onScroll.bind(this));
    }
  
    onScroll: () => void = () => {
        const container = this.containerRef.current;
        const wrapper = this.wrapperRef.current;
        if (container == null || wrapper == null) return;
    
        const bounds = container.getBoundingClientRect();
        const wrapperHeight = wrapper.getBoundingClientRect().height;
        const max = bounds.height / wrapperHeight;
        const progress = max - bounds.bottom / wrapperHeight;
        const fraction = Math.min(1, Math.max(0, progress / (max - 1)));
        this.setState(prevState => ({ ...prevState, progress: fraction }));
        //this.updateProgress(fraction);
    
    };
  
    onFirstFrameLoaded: (() => void) = () => {
        this.setState(
            prevState => ({
                ...prevState,
                firstFrameLoaded: true,
            }),
        );
    };
  
    render(): React$Element<"div"> {
        const {frames} = this.props;
        const {progress, firstFrameLoaded} = this.state;
        const className = classNameOf(
            "innovation-animation",
            {
                "first-frame-loaded": firstFrameLoaded,
            },
        );
    
        return (
            <div className="innovation-animation-container" ref={this.containerRef}>
                <div className="innovation-animation-wrapper" ref={this.wrapperRef}>
                    <div className={className}>
                        <InnovationBackground
                            progress={progress}
                            frames={frames}
                            firstFrameLoaded={this.onFirstFrameLoaded.bind(this)}
                        />
                        <InnovationForeground progress={progress} />
                        <div className="scroll-cursor-wrapper">
                            <ScrollCursor progress={progress} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default InnovationAnimation;
